import { computed } from 'vue'
import { useTheme } from 'vuetify'
import { NoteColor } from '@/utils/constants'

// Light theme note background colors
export const NOTE_COLORS_LIGHT = {
  [NoteColor.YELLOW]: '#fffec8',
  [NoteColor.GREEN]: '#d8f8dc',
  [NoteColor.TURQUOISE]: '#c3feff',
  [NoteColor.PURPLE]: '#cfd1ff',
  [NoteColor.PINK]: '#fbd5ff',
  [NoteColor.RED]: '#ffd4db',
  [NoteColor.ORANGE]: '#ffe7c8',
  [NoteColor.GREY]: '#eeeeee',
  [NoteColor.BODY]: '#ffffff',
}

// Dark theme note background colors
export const NOTE_COLORS_DARK = {
  [NoteColor.YELLOW]: '#424104',
  [NoteColor.GREEN]: '#052c0a',
  [NoteColor.TURQUOISE]: '#094445',
  [NoteColor.PURPLE]: '#250948',
  [NoteColor.PINK]: '#3b0740',
  [NoteColor.RED]: '#35050d',
  [NoteColor.ORANGE]: '#522b08',
  [NoteColor.GREY]: '#222222',
  [NoteColor.BODY]: '#090e10',
}

// Light theme note icon colors (darker shades for contrast on light backgrounds)
export const NOTE_ICON_COLORS_LIGHT = {
  [NoteColor.YELLOW]: '#B8860B',
  [NoteColor.GREEN]: '#2E7D32',
  [NoteColor.TURQUOISE]: '#00838F',
  [NoteColor.PURPLE]: '#7B1FA2',
  [NoteColor.PINK]: '#C2185B',
  [NoteColor.RED]: '#C62828',
  [NoteColor.ORANGE]: '#E65100',
  [NoteColor.GREY]: '#616161',
  [NoteColor.BODY]: '#809aa6',
}

// Dark theme note icon colors (lighter shades for contrast on dark backgrounds)
export const NOTE_ICON_COLORS_DARK = {
  [NoteColor.YELLOW]: '#FFD54F',
  [NoteColor.GREEN]: '#81C784',
  [NoteColor.TURQUOISE]: '#4DD0E1',
  [NoteColor.PURPLE]: '#CE93D8',
  [NoteColor.PINK]: '#F48FB1',
  [NoteColor.RED]: '#EF9A9A',
  [NoteColor.ORANGE]: '#FFB74D',
  [NoteColor.GREY]: '#BDBDBD',
  [NoteColor.BODY]: '#bfd0d9',
}

// Map of note colors to 1200×675 OG/meta preview image filenames.
// Must stay in sync with backend NOTE_OG_IMAGE_MAP in libs/utils/src/note/index.ts.
const NOTE_META_IMAGE_MAP = {
  [NoteColor.YELLOW]: 'og_note_yellow_big.png',
  [NoteColor.GREEN]: 'og_note_green_big.png',
  [NoteColor.TURQUOISE]: 'og_note_turquoise_big.png',
  [NoteColor.PURPLE]: 'og_note_purple_big.png',
  [NoteColor.PINK]: 'og_note_pink_big.png',
  [NoteColor.RED]: 'og_note_red_big.png',
  [NoteColor.ORANGE]: 'og_note_orange_big.png',
  [NoteColor.GREY]: 'og_note_grey_big.png',
  [NoteColor.BODY]: 'og_note_body_big.png',
}

/**
 * Get the CDN URL for a note's OG/meta preview image based on its color.
 * @param {string} colorKey - The note color key
 * @returns {string} Full CDN URL for the note preview image
 */
export function getNoteMetaImageUrl(colorKey) {
  const filename = NOTE_META_IMAGE_MAP[colorKey] || 'og_note_yellow_big.png'
  return `${CONFIG.cdnUrl}/${filename}`
}

// Array of all note color keys for iteration
export const NOTE_COLOR_KEYS = Object.values(NoteColor)

/**
 * Factory function to get color hex based on theme
 * @param {string} colorKey - The note color key (e.g., 'yellow', 'green')
 * @param {boolean} isDark - Whether dark theme is active
 * @returns {string} The hex color value
 */
export function getNoteColor(colorKey, isDark) {
  return isDark ? NOTE_COLORS_DARK[colorKey] : NOTE_COLORS_LIGHT[colorKey]
}

/**
 * Factory function to get icon color hex based on theme
 * @param {string} colorKey - The note color key (e.g., 'yellow', 'green')
 * @param {boolean} isDark - Whether dark theme is active
 * @returns {string} The hex color value for the icon
 */
export function getNoteIconColor(colorKey, isDark) {
  return isDark ? NOTE_ICON_COLORS_DARK[colorKey] : NOTE_ICON_COLORS_LIGHT[colorKey]
}

/**
 * Get all note colors for the specified theme
 * @param {boolean} isDark - Whether dark theme is active
 * @returns {Object} Map of color keys to hex values
 */
export function getNoteColors(isDark) {
  return isDark ? NOTE_COLORS_DARK : NOTE_COLORS_LIGHT
}

/**
 * Composable for reactive theme-aware note colors
 * @returns {Object} Reactive color utilities
 */
export function useNoteColors() {
  const theme = useTheme()

  const isDark = computed(() => theme.global.current.value.dark)

  const colors = computed(() => getNoteColors(isDark.value))

  const getColor = colorKey => computed(() => getNoteColor(colorKey, isDark.value))

  const colorOptions = computed(() =>
    NOTE_COLOR_KEYS.map(key => ({
      key,
      hex: getNoteColor(key, isDark.value),
    }))
  )

  return {
    isDark,
    colors,
    getColor,
    colorOptions,
    getNoteColor: (colorKey) => getNoteColor(colorKey, isDark.value),
    getNoteIconColor: (colorKey) => getNoteIconColor(colorKey, isDark.value),
  }
}
